/**
 * Moldura da aplicação: barra superior, navegação por perfil e tema por tela — Nexora Grid (frontend).
 *
 * Criado em: 16/10/2026 · Última modificação: 02/11/2026 · Versão: 1.7.0
 *
 * Tema (documento 14): CCO, Rede, Cadastro e Videowall usam `data-tema="cco"` (ISA-101); o SCADA 3D usa
 * `data-tema="scada3d"` (JARVIS). Com `?embutido=1` (telas do videowall) a barra some; com `&janela=1` (tela levada a outro monitor) aparece o botão Tela cheia.
 * v1.7 (Sprint 45): logomarca e nome pela marca do integrador (`useMarca`); seletor do modo de exibição na barra.
 * D-010: nomes das áreas pela fonte única `nomes.ts` (o nome do menu é o padrão do sistema).
 */
import { NOMES } from "./nomes";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useSearchParams } from "react-router-dom";
import { MenuCompleto } from "./Menu";
import { nomePapeis, pode } from "./permissoes";
import { useSessao } from "./sessao";
import { Avisos } from "./componentes/Tela";
import { Logomarca, MODOS, sufixoProduto, useMarca, type ModoExibicao } from "./marca";

export function Layout() {
  const { usuario, empresa, escolherEmpresa, sair } = useSessao();
  const { marca, modo, trocarModo } = useMarca();
  const local = useLocation();
  const [parametros] = useSearchParams();
  const tema = local.pathname.startsWith("/scada3d") ? "scada3d" : "cco";
  const embutido = parametros.get("embutido") === "1";
  const janelaVideowall = embutido && parametros.get("janela") === "1";
  const [menuAberto, setMenuAberto] = useState(false);
  useEffect(() => setMenuAberto(false), [local.pathname]);
  if (!usuario) return null;
  // Descarta dados e formulários carregados quando muda a operadora ou o acesso efetivo.
  const chaveAcesso = JSON.stringify([usuario.id, usuario.perfil, usuario.empresa, empresa, usuario.empresas, usuario.papeis,
    usuario.capacidades, usuario.funcoes_treinamento, usuario.perfis_validacao]);
  const empresas = usuario.empresas.includes("*")
    ? Object.keys(usuario.nomes_empresas ?? {}).sort()
    : usuario.empresas.filter((e) => e !== "*");
  // a aba abre a primeira seção que a pessoa pode ver (empresas para a Nexora; usuários para o administrador)
  const administracao = pode.verEstrutura(usuario) ? [{ para: "/admin/empresas", texto: NOMES.administracao }]
    : pode.verUsuarios(usuario) ? [{ para: "/admin/usuarios", texto: NOMES.administracao }]
    : pode.editarFeriados(usuario) || pode.importarFeriadosNacionais(usuario) ? [{ para: "/admin/feriados", texto: NOMES.administracao }]
    : pode.importarReferencias(usuario) ? [{ para: "/admin/referencias", texto: NOMES.administracao }] : [];
  const links = [
    { para: "/cco", texto: NOMES.cco },
    { para: "/rede", texto: NOMES.rede },
    { para: "/scada3d", texto: NOMES.scada3d },
    ...(pode.verCadastro(usuario) ? [{ para: "/cadastro", texto: NOMES.cadastro }] : []),
    ...(pode.verPaineis(usuario) ? [{ para: "/meus-paineis", texto: NOMES.paineis }] : []),
    { para: "/videowall", texto: NOMES.videowall },
    ...(pode.verTreinamento(usuario) ? [{ para: "/treinamento", texto: NOMES.treinamento }] : []),
    ...administracao,  // US-1804: administrador do cliente e suporte
  ];
  return (
    <div className="nx-tela app" data-tema={tema}>
      {!embutido && (
        <header className="nx-barra barra-app">
          <button type="button" className="nx-botao botao-menu" aria-expanded={menuAberto} aria-controls="menu-lateral"
            onClick={() => setMenuAberto(!menuAberto)}>☰ Menu</button>
          <span className="nx-marca marca-app"><Logomarca /> {sufixoProduto(marca) && <span>{sufixoProduto(marca).toUpperCase()}</span>}</span>
          <nav aria-label="Telas">
            {links.map((l) => <NavLink key={l.para} to={l.para} className="aba">{l.texto}</NavLink>)}
          </nav>
          <span className="espaco" />
          {empresas.length > 1 ? (
            <select aria-label="Operadora" value={empresa ?? ""} onChange={(e) => escolherEmpresa(e.target.value)}>
              {empresas.map((e) => <option key={e} value={e}>{usuario.nomes_empresas?.[e] ?? e}</option>)}
            </select>
          ) : <span className="nx-suave" title={empresa ?? undefined}>{(empresa && usuario.nomes_empresas?.[empresa]) || empresa || "—"}</span>}
          <span className="usuario" title={nomePapeis(usuario)}>{usuario.nome}<small className="nx-suave"> · {nomePapeis(usuario)}</small></span>
          <select aria-label="Modo de exibição" title="Modo de exibição (vale em qualquer computador)" value={modo}
            onChange={(e) => void trocarModo(e.target.value as ModoExibicao)}>
            {MODOS.map((m) => <option key={m.valor} value={m.valor}>{m.texto}</option>)}
          </select>
          <button type="button" className="nx-botao" onClick={() => void sair()}>Sair</button>
        </header>
      )}
      {menuAberto && !embutido && (
        <>
          <div className="fundo-menu" onClick={() => setMenuAberto(false)} aria-hidden />
          <aside id="menu-lateral" className="menu-lateral" aria-label="Menu">
            <MenuCompleto usuario={usuario} aoEscolher={() => setMenuAberto(false)} />
          </aside>
        </>
      )}
      {janelaVideowall && (
        <button type="button" className="vw-tela-cheia" title="Tela cheia neste monitor"
          onClick={() => void (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen()).catch(() => undefined)}>
          Tela cheia</button>
      )}
      <main className={`conteudo-app${embutido ? " embutido" : ""}`}>
        <Outlet key={chaveAcesso} />
      </main>
      <Avisos />
    </div>
  );
}
